import {
  type CollectionOfFiltersCard,
  type RestaurantTileContent,
} from '@/components/features/home/HomeRoot/CollectionOfFilters/CollectionOfFilters';
import {CuisineTaxonEnum} from '@/components/features/search/filters/FiltersUtils/cuisines';
import {type SearchQuery} from '@/graphql/types';
import type RequiredPath from '@/utils/RequiredPath';

// Builds the three restaurant cards for the Collection of Filters carousel.
//
// Order matters — each step accumulates into placedBrandIds so no brand appears in two cards.
//   1. Popular Near You:  Our 15 curated top brands in listed order, then fill any remaining
//                         slots with the top search results. Renders the brand LOGO as the
//                         tile image.
//   2. Cater Breakfast:   Top of the breakfast query, deduped against Popular Near You.
//   3. Team Lunch:        Top of the popular query, deduped against everything placed in
//                         Popular Near You + Cater Breakfast, AND excluding every brand
//                         tagged breakfast (even those not placed anywhere).
//
// The caller (useCollectionOfFilters) hides the entire carousel if any card ends up with
// fewer than TILES_PER_CARD items.

export const TILES_PER_CARD = 4;
export const POPULAR_QUERY_LIMIT = 50;
export const BREAKFAST_QUERY_LIMIT = 20;

// This list is popular brands that should be prioritized in the "Popular Near You" card.
export const CURATED_TOP_BRAND_IDS: readonly string[] = [
  '3081', // Panera Bread
  '3005', // Olive Garden
  '3344', // QDOBA Mexican Eats
  '916', // Chick-fil-A
  '3045', // Outback Steakhouse
  '2679', // McAlister's Deli
  '761', // Carrabba's Italian Grill
  '2801', // Moe's Southwest Grill
  '2188', // Jersey Mike's Subs
  '29997', // Red Robin
  '1601', // Firehouse Subs
  '3921', // Subway
  '3306', // Potbelly Sandwich Shop
  '23216', // sweetgreen
  '3051', // PF Chang's
];

export type Node = NonNullable<
  RequiredPath<SearchQuery, ['marketplaceSearchV2', 'caterers', 'nodes', number]>
>;

const toRestaurantTileContent = (node: Node): RestaurantTileContent | null => {
  const brandId = node.searchResultBrand?.id;
  if (!brandId) return null;
  return {
    id: brandId,
    name: node.name,
    imageUrl: node.imageSrc ?? '',
    ...(node.searchResultBrand?.logoImageSrc && {
      logoImageUrl: node.searchResultBrand.logoImageSrc,
    }),
  };
};

const pickTop = (
  nodes: readonly Node[],
  count: number,
  used: Set<string>,
  excluded: ReadonlySet<string> = new Set(),
): RestaurantTileContent[] => {
  const items: RestaurantTileContent[] = [];
  for (const node of nodes) {
    if (items.length >= count) break;
    const brandId = node.searchResultBrand?.id;
    if (!brandId || used.has(brandId) || excluded.has(brandId)) continue;
    const item = toRestaurantTileContent(node);
    if (item) {
      items.push(item);
      used.add(brandId);
    }
  }
  return items;
};

const pickCuratedThenTop = (
  nodes: readonly Node[],
  curatedBrandIds: readonly string[],
  count: number,
  used: Set<string>,
): RestaurantTileContent[] => {
  const byBrand = new Map<string, Node>();
  nodes.forEach(node => {
    const id = node.searchResultBrand?.id;
    if (id && !byBrand.has(id)) byBrand.set(id, node);
  });

  const curated: RestaurantTileContent[] = [];
  for (const brandId of curatedBrandIds) {
    if (curated.length >= count) break;
    if (used.has(brandId)) continue;
    const node = byBrand.get(brandId);
    if (!node) continue; // Top brand isn't in the popular pool near this user
    const item = toRestaurantTileContent(node);
    if (item) {
      curated.push(item);
      used.add(brandId);
    }
  }

  return [...curated, ...pickTop(nodes, count - curated.length, used)];
};

export const filterNonNullNodes = (
  nodes: readonly (Node | null | undefined)[] | null | undefined,
): readonly Node[] => (nodes ?? []).filter((n): n is Node => n != null);

const useLogoAsTileImage = (item: RestaurantTileContent): RestaurantTileContent => ({
  ...item,
  imageUrl: item.logoImageUrl ?? item.imageUrl,
  logoImageUrl: undefined,
});

export const buildCards = (
  popularNodes: readonly Node[],
  breakfastNodes: readonly Node[],
): {
  cards: readonly [CollectionOfFiltersCard, CollectionOfFiltersCard, CollectionOfFiltersCard];
} => {
  // Every brand tagged BREAKFAST at this address — Team Lunch excludes all of them.
  const allBreakfastBrandIds = new Set<string>(
    breakfastNodes.map(n => n.searchResultBrand?.id).filter((id): id is string => Boolean(id)),
  );

  // Brands already placed in a card. Each picker adds to it so no brand appears in two cards.
  const placedBrandIds = new Set<string>();

  const popularNearYouItems = pickCuratedThenTop(
    popularNodes,
    CURATED_TOP_BRAND_IDS,
    TILES_PER_CARD,
    placedBrandIds,
  ).map(useLogoAsTileImage);

  const breakfastItems = pickTop(breakfastNodes, TILES_PER_CARD, placedBrandIds);

  const teamLunchItems = pickTop(
    popularNodes,
    TILES_PER_CARD,
    placedBrandIds,
    allBreakfastBrandIds,
  );

  const popularNearYouCard: CollectionOfFiltersCard = {
    header: 'collectionOfFilters.cardHeader.popularNearYou',
    items: popularNearYouItems,
    tileVariant: 'logo',
  };

  const teamLunchCard: CollectionOfFiltersCard = {
    header: 'collectionOfFilters.cardHeader.teamLunch',
    items: teamLunchItems,
  };

  const caterBreakfastCard: CollectionOfFiltersCard = {
    header: 'collectionOfFilters.cardHeader.breakfastFaves',
    browseAllFilterParams: {cuisineOption: CuisineTaxonEnum.Breakfast},
    items: breakfastItems,
  };

  const cards: readonly [
    CollectionOfFiltersCard,
    CollectionOfFiltersCard,
    CollectionOfFiltersCard,
  ] = [teamLunchCard, popularNearYouCard, caterBreakfastCard];

  return {cards};
};
